import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowRightLeft,
  CalendarCheck,
  Check,
  CheckCircle2,
  CheckCheck,
  ChevronLeft,
  Download,
  Eraser,
  Lock,
  LockOpen,
  Pencil,
  RotateCcw,
  Scale,
  Search,
  ShieldAlert,
  Sparkles,
  X,
} from 'lucide-react';
import {
  api,
  canEdit,
  type AuthUser,
  type EventStatus,
  type SpecialAttendance,
  type SpecialEventAssignment,
  type SpecialEventDetail as SpecialEventDetailPayload,
  type SpecialEventPlayer,
  type SpecialEventTeam,
} from '@/lib/api';
import { cn, formatPower } from '@/lib/utils';
import { renderNodeToPng } from '@/lib/downloadPng';
import { SpecialEventTeamCard, SpecialEventOverview } from '@/components/SpecialEventExports';
import { SERVER_UTC_OFFSET_MINUTES } from '@/lib/serverTime';

const STATUS_STYLES: Record<EventStatus, string> = {
  planned: 'bg-indigo-700/50 text-indigo-100',
  completed: 'bg-emerald-700/50 text-emerald-100',
  skipped: 'bg-slate-700 text-slate-300',
};

const ATTENDANCE_LABEL: Record<SpecialAttendance, string> = {
  showed: '✓',
  rulebreaker: '⚠',
  no_show: '✕',
};

const ATTENDANCE_STYLE: Record<SpecialAttendance, string> = {
  showed: 'bg-emerald-700/50 text-emerald-100',
  rulebreaker: 'bg-amber-700/50 text-amber-100',
  no_show: 'bg-red-700/50 text-red-100',
};

function playerLabel(p: SpecialEventPlayer | null) {
  if (!p) return '';
  return (p.displayName?.trim() || p.name).trim();
}

function SpecialPlayerPicker({
  players,
  selectedId,
  onSelect,
  disabled,
  placeholder = 'Assign player…',
  compact,
}: {
  players: SpecialEventPlayer[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return players;
    return players.filter((p) => {
      const label = (p.displayName?.trim() || p.name).toLowerCase();
      return label.includes(q) || p.name.toLowerCase().includes(q);
    });
  }, [players, query]);
  const selected = players.find((p) => p.id === selectedId) ?? null;

  useEffect(() => {
    if (!open) return;
    setHighlight(0);
    setQuery('');
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const pick = (id: number | null) => {
    onSelect(id);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center justify-between gap-1 rounded border px-2 text-left',
          compact ? 'py-0.5 text-xs' : 'py-1 text-sm',
          selected
            ? 'border-slate-700 bg-slate-950 hover:border-slate-600'
            : 'border-dashed border-slate-700 bg-slate-950/60 text-slate-500 hover:border-slate-600',
          disabled && 'opacity-60',
        )}
      >
        <span className="truncate">
          {selected ? playerLabel(selected) : placeholder}
        </span>
        <Search size={12} className="shrink-0 text-slate-500" />
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-xl">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight((h) => Math.min(filtered.length - 1, h + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight((h) => Math.max(0, h - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                const p = filtered[highlight];
                if (p) pick(p.id);
              } else if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
            placeholder="Search player…"
            className="w-full border-b border-slate-800 bg-slate-950 px-3 py-2 text-sm focus:outline-none"
          />
          <ul className="max-h-56 overflow-y-auto py-1">
            {selectedId != null && (
              <li>
                <button
                  type="button"
                  onClick={() => pick(null)}
                  className="w-full px-3 py-1.5 text-left text-xs text-slate-400 hover:bg-slate-800"
                >
                  — clear —
                </button>
              </li>
            )}
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500">No match.</li>
            )}
            {filtered.map((p, i) => {
              const isSel = p.id === selectedId;
              const isHi = i === highlight;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => pick(p.id)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm',
                      isHi && 'bg-slate-800',
                      isSel && 'text-indigo-300',
                    )}
                  >
                    <span className="truncate">
                      {playerLabel(p)}
                      {p.displayName && p.displayName !== p.name && (
                        <span className="ml-2 text-xs text-slate-500">{p.name}</span>
                      )}
                    </span>
                    <span className="shrink-0 text-[10px] tabular-nums text-slate-500">
                      {formatPower(p.totalPower ?? 0)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function SpecialEventDetail({ user, id }: { user: AuthUser; id: number }) {
  const editable = canEdit(user.role);
  const qc = useQueryClient();
  const dataQuery = useQuery({
    queryKey: ['special-event', id],
    queryFn: () => api.getSpecialEvent(id),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['special-event', id] });
    qc.invalidateQueries({ queryKey: ['events'] });
  };

  const setStatus = useMutation({
    mutationFn: (status: EventStatus) => api.patchSpecialEvent(id, { status }),
    onSuccess: invalidate,
  });

  const setPlanningDone = useMutation({
    mutationFn: (planningDone: boolean) =>
      api.patchSpecialEvent(id, { planningDone }),
    onSuccess: invalidate,
  });

  const patchEvent = useMutation({
    mutationFn: (
      body: Partial<{ customName: string; eventDate: string; startsAt: string | null }>,
    ) => api.patchSpecialEvent(id, body),
    onSuccess: invalidate,
  });

  const distribute = useMutation({
    mutationFn: (mode: 'fill' | 'force') => api.distributeSpecialEvent(id, { mode }),
    onSuccess: invalidate,
  });

  const assignSlot = useMutation({
    mutationFn: (body: Parameters<typeof api.upsertSpecialAssignment>[1]) =>
      api.upsertSpecialAssignment(id, body),
    onSuccess: invalidate,
  });

  const moveSlot = useMutation({
    mutationFn: (body: Parameters<typeof api.moveSpecialAssignment>[1]) =>
      api.moveSpecialAssignment(id, body),
    onSuccess: invalidate,
  });

  const moveToTeam = useMutation({
    mutationFn: ({ playerId, toTeamId }: { playerId: number; toTeamId: number }) => {
      const data = dataQuery.data!;
      const target = data.teams.find((t) => t.id === toTeamId)!;
      const empty = target.assignments.find((a) => a.playerId == null);
      const maxPos = target.assignments.reduce((m, a) => Math.max(m, a.position), 0);
      const position = empty ? empty.position : maxPos + 1;
      return api.upsertSpecialAssignment(id, {
        teamId: toTeamId,
        position,
        playerId,
      });
    },
    onSuccess: invalidate,
  });

  const patchTeam = useMutation({
    mutationFn: ({
      teamId,
      body,
    }: {
      teamId: number;
      body: Parameters<typeof api.patchSpecialTeam>[2];
    }) => api.patchSpecialTeam(id, teamId, body),
    onSuccess: invalidate,
  });

  const setAttendance = useMutation({
    mutationFn: (rows: { assignmentId: number; attendance: SpecialAttendance | null }[]) =>
      api.setSpecialAttendance(id, rows),
    onSuccess: invalidate,
  });

  const markAllAttended = useMutation({
    mutationFn: () => {
      const rows = (dataQuery.data?.teams ?? [])
        .flatMap((t) => t.assignments)
        .filter((a) => a.playerId != null && a.attendance == null)
        .map((a) => ({ assignmentId: a.id, attendance: 'showed' as SpecialAttendance }));
      return api.setSpecialAttendance(id, rows);
    },
    onSuccess: invalidate,
  });

  const [editHeader, setEditHeader] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [editingWeights, setEditingWeights] = useState(false);
  const overviewRef = useRef<HTMLDivElement>(null);
  const teamCardRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const clearAssignments = useMutation({
    mutationFn: () => api.clearSpecialAssignments(id),
    onSuccess: invalidate,
  });

  if (dataQuery.isLoading || !dataQuery.data) {
    return <div className="text-slate-500">Loading…</div>;
  }
  const data = dataQuery.data;
  const isCompleted = data.event.status === 'completed';
  const isPlanningDone = data.event.planningDoneAt != null;
  // Team/slot editing is locked once planning is done or the event is
  // completed. Attendance stays editable in both states so R4+ can still
  // record participation after the fact.
  const readOnly = !editable || isCompleted || isPlanningDone;
  const attendanceLocked = !editable;
  const pendingAttendance = data.teams
    .flatMap((t) => t.assignments)
    .filter((a) => a.playerId != null && a.attendance == null).length;

  return (
    <div className="space-y-4">
      <div>
        <Link
          to="/events"
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white"
        >
          <ChevronLeft size={14} /> Back to events
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <div className="text-lg font-semibold">
          {data.event.customName ?? 'Special event'}
        </div>
        <div className="text-sm text-slate-400">
          {data.event.eventDate}
          {data.event.startsAt ? ` · ${data.event.startsAt}` : ''}
        </div>
        {data.event.startsAt && !isCompleted && (
          <SpecialEventCountdown
            eventDate={data.event.eventDate}
            startsAt={data.event.startsAt}
          />
        )}
        <span
          className={cn(
            'rounded px-2 py-0.5 text-xs capitalize',
            STATUS_STYLES[data.event.status],
          )}
        >
          {data.event.status}
        </span>
        {isPlanningDone && !isCompleted && (
          <span className="rounded bg-sky-700/50 px-2 py-0.5 text-xs text-sky-100">
            Planning done — participation only
          </span>
        )}
        {pendingAttendance > 0 && (
          <span className="rounded bg-amber-700/50 px-2 py-0.5 text-xs text-amber-100">
            {pendingAttendance} pending attendance
          </span>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {editable && !readOnly && (
            <>
              <button
                onClick={() => setEditHeader(true)}
                className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
              >
                <Pencil size={12} /> Edit
              </button>
              <button
                onClick={() => distribute.mutate('fill')}
                disabled={distribute.isPending}
                className="inline-flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-xs hover:bg-indigo-500 disabled:opacity-50"
              >
                <Sparkles size={12} /> Distribute
              </button>
              <button
                onClick={() => {
                  if (confirm('Force redistribute? Locked slots stay, everything else is re-placed.')) {
                    distribute.mutate('force');
                  }
                }}
                disabled={distribute.isPending}
                className="inline-flex items-center gap-1 rounded border border-red-700 bg-red-900/30 px-2 py-1 text-xs text-red-200 hover:bg-red-900/60 disabled:opacity-50"
              >
                <RotateCcw size={12} /> Force
              </button>
              <button
                onClick={() => setEditingWeights(true)}
                className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
              >
                <Scale size={12} /> Weights…
              </button>
              <button
                onClick={() => {
                  if (confirm('Clear every team? All player assignments and locks are removed.')) {
                    clearAssignments.mutate();
                  }
                }}
                disabled={clearAssignments.isPending}
                className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
              >
                <Eraser size={12} /> Clear all teams
              </button>
            </>
          )}
          <button
            onClick={() => setShowExport(true)}
            className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
          >
            <Download size={12} /> Export
          </button>
          {editable && !isCompleted && !isPlanningDone && (
            <button
              onClick={() => setPlanningDone.mutate(true)}
              disabled={setPlanningDone.isPending}
              className="inline-flex items-center gap-1 rounded border border-sky-700 bg-sky-900/30 px-2 py-1 text-xs text-sky-200 hover:bg-sky-900/60 disabled:opacity-50"
            >
              <CalendarCheck size={12} /> Mark as planned
            </button>
          )}
          {editable && isPlanningDone && !isCompleted && (
            <button
              onClick={() => setPlanningDone.mutate(false)}
              disabled={setPlanningDone.isPending}
              className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
            >
              Reopen planning
            </button>
          )}
          {editable && pendingAttendance > 0 && (
            <button
              onClick={() => {
                if (
                  confirm(
                    `Mark the ${pendingAttendance} remaining assigned player(s) as attended?`,
                  )
                ) {
                  markAllAttended.mutate();
                }
              }}
              disabled={markAllAttended.isPending}
              className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/30 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-900/60 disabled:opacity-50"
            >
              <CheckCheck size={12} /> All attended
            </button>
          )}
          {editable && !isCompleted && (
            <button
              onClick={() => setStatus.mutate('completed')}
              className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/30 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-900/60"
            >
              <CheckCircle2 size={12} /> Mark completed
            </button>
          )}
          {editable && isCompleted && (
            <button
              onClick={() => setStatus.mutate('planned')}
              className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700"
            >
              Reopen
            </button>
          )}
        </div>
      </div>

      {editingWeights && (
        <EditWeightsModal
          teams={data.teams}
          onSave={async (rows) => {
            await Promise.all(
              rows.map((r) =>
                api.patchSpecialTeam(id, r.teamId, { strengthShare: r.strengthShare }),
              ),
            );
            invalidate();
            setEditingWeights(false);
          }}
          onClose={() => setEditingWeights(false)}
        />
      )}
      {editHeader && (
        <EditHeaderModal
          initial={{
            customName: data.event.customName ?? '',
            eventDate: data.event.eventDate,
            startsAt: data.event.startsAt ?? '',
          }}
          onSave={(body) => {
            patchEvent.mutate(body, { onSuccess: () => setEditHeader(false) });
          }}
          pending={patchEvent.isPending}
          onClose={() => setEditHeader(false)}
        />
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data.teams.map((team) => (
          <TeamPanel
            key={team.id}
            team={team}
            unassigned={data.unassigned}
            readOnly={readOnly}
            onAssign={(pos, playerId) =>
              assignSlot.mutate({ teamId: team.id, position: pos, playerId })
            }
            onToggleLock={(a) =>
              assignSlot.mutate({
                teamId: team.id,
                position: a.position,
                playerId: a.playerId,
                locked: !a.locked,
              })
            }
            onMoveToTeam={(playerId, toTeamId) =>
              moveToTeam.mutate({ playerId, toTeamId })
            }
            onLeader={(playerId) => patchTeam.mutate({ teamId: team.id, body: { leaderPlayerId: playerId } })}
            onTask={(task) => patchTeam.mutate({ teamId: team.id, body: { task } })}
            onSetAttendance={(assignmentId, attendance) =>
              setAttendance.mutate([{ assignmentId, attendance }])
            }
            onEditTeam={(body) => patchTeam.mutate({ teamId: team.id, body })}
            attendanceLocked={attendanceLocked}
            allTeams={data.teams}
          />
        ))}
      </div>

      {data.unassigned.length > 0 && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
          <div className="mb-2 text-xs uppercase tracking-wider text-slate-500">
            Unassigned ({data.unassigned.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {data.unassigned.map((p) => (
              <span
                key={p.id}
                className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-200"
                title={`M1 ${formatPower(p.m1Power ?? 0)} · Total ${formatPower(p.totalPower ?? 0)}`}
              >
                {playerLabel(p)}
              </span>
            ))}
          </div>
        </div>
      )}

      {showExport && (
        <ExportModal
          data={data}
          overviewRef={overviewRef}
          teamCardRefs={teamCardRefs}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
}

function TeamPanel({
  team,
  unassigned,
  readOnly,
  attendanceLocked,
  onAssign,
  onToggleLock,
  onMoveToTeam,
  onLeader,
  onTask,
  onSetAttendance,
  onEditTeam,
  allTeams,
}: {
  team: SpecialEventTeam;
  unassigned: SpecialEventPlayer[];
  readOnly: boolean;
  onAssign: (position: number, playerId: number | null) => void;
  onToggleLock: (a: SpecialEventAssignment) => void;
  onMoveToTeam: (playerId: number, toTeamId: number) => void;
  onLeader: (playerId: number | null) => void;
  onTask: (task: string | null) => void;
  onSetAttendance: (assignmentId: number, attendance: SpecialAttendance | null) => void;
  onEditTeam: (body: Partial<{ name: string | null; capacity: number; strengthShare: number }>) => void;
  attendanceLocked?: boolean;
  allTeams: SpecialEventTeam[];
}) {
  const [editingTask, setEditingTask] = useState(false);
  const [taskDraft, setTaskDraft] = useState(team.task ?? '');
  const [moving, setMoving] = useState<SpecialEventAssignment | null>(null);
  const [editingTeam, setEditingTeam] = useState(false);
  // Show the raw share value out of the sum-of-shares, so the UI number lines
  // up with what the user typed in the create form.
  const shareSum = allTeams.reduce((s, t) => s + t.strengthShare, 0) || 1;

  // Leader picker draws from the team's own players plus the unassigned pool.
  // Picking an unassigned player as leader auto-places them in the team on
  // the server, so this stays populated even after "Clear all teams".
  const leaderOptions: SpecialEventPlayer[] = [
    ...team.assignments.filter((a) => a.player).map((a) => a.player!),
    ...unassigned,
    ...(team.leader && !team.assignments.some((a) => a.playerId === team.leader!.id)
      ? [team.leader]
      : []),
  ];

  // Slot pool: everyone unassigned + the player currently sitting in that
  // very slot (so they show up in their own row). We build it per row below.
  const slotPoolBase = unassigned;

  const dev = team.totals.deviation;
  const devPct = team.totals.targetTotal > 0 ? (dev / team.totals.targetTotal) * 100 : 0;
  const overCapacity = team.totals.assigned > team.capacity;
  const underCapacity = team.totals.assigned < team.capacity;
  const capacityFilled = team.totals.assigned === team.capacity;

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40">
      <div className="border-b border-slate-800 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="font-semibold">
            {team.name?.trim() || `Team ${team.teamIndex}`}
          </div>
          <span
            className={cn(
              'rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
              capacityFilled
                ? 'bg-slate-800 text-slate-400'
                : overCapacity
                  ? 'bg-red-900/60 text-red-100'
                  : underCapacity
                    ? 'bg-red-900/40 text-red-200'
                    : 'bg-slate-800 text-slate-400',
            )}
          >
            {team.totals.assigned}/{team.capacity}
          </span>
          <span className="rounded bg-slate-800 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-400">
            {Math.round(team.strengthShareNormalized * 100)}%
          </span>
          {!readOnly && (
            <button
              onClick={() => setEditingTeam(true)}
              title="Rename, resize or reweight this team"
              className="rounded border border-slate-700 px-1.5 py-0.5 text-[11px] text-slate-300 hover:bg-slate-800"
            >
              <Pencil size={11} />
            </button>
          )}
          <span
            className={cn(
              'rounded px-2 py-0.5 text-[10px] font-semibold',
              devPct >= -5 && devPct <= 5
                ? 'bg-emerald-900/40 text-emerald-200'
                : Math.abs(devPct) < 15
                  ? 'bg-amber-900/40 text-amber-200'
                  : 'bg-red-900/40 text-red-200',
            )}
            title={`Target Σ total ${formatPower(team.totals.targetTotal)}`}
          >
            {dev >= 0 ? '+' : ''}
            {devPct.toFixed(1)}%
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-400">
          <span>Σ M1 <strong className="text-slate-200">{formatPower(team.totals.sumM1)}</strong></span>
          <span>Σ Total <strong className="text-slate-200">{formatPower(team.totals.sumTotal)}</strong></span>
          <span className="ml-auto flex min-w-[180px] items-center gap-1">
            <span className="shrink-0 text-slate-400">Leader:</span>
            <SpecialPlayerPicker
              players={leaderOptions}
              selectedId={team.leaderPlayerId}
              onSelect={onLeader}
              disabled={readOnly}
              placeholder="—"
              compact
            />
          </span>
        </div>
        <div className="mt-2 flex items-start gap-2 text-xs">
          {editingTask ? (
            <div className="flex flex-1 items-start gap-1">
              <textarea
                value={taskDraft}
                onChange={(e) => setTaskDraft(e.target.value)}
                rows={2}
                placeholder="Team task (freetext, appears on export image)"
                className="flex-1 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs"
              />
              <button
                onClick={() => {
                  onTask(taskDraft.trim() === '' ? null : taskDraft.trim());
                  setEditingTask(false);
                }}
                className="rounded bg-indigo-600 px-2 py-1 text-xs hover:bg-indigo-500"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setTaskDraft(team.task ?? '');
                  setEditingTask(false);
                }}
                className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
              >
                Cancel
              </button>
            </div>
          ) : team.task ? (
            <>
              <div className="flex-1 rounded border border-fuchsia-800/50 bg-fuchsia-900/20 px-2 py-1 text-fuchsia-100">
                <span className="mr-1 text-[10px] font-semibold uppercase tracking-wider text-fuchsia-300">
                  Task
                </span>
                {team.task}
              </div>
              {!readOnly && (
                <button
                  onClick={() => setEditingTask(true)}
                  className="rounded border border-slate-700 px-1.5 py-1 text-slate-300 hover:bg-slate-800"
                >
                  <Pencil size={12} />
                </button>
              )}
            </>
          ) : (
            !readOnly && (
              <button
                onClick={() => setEditingTask(true)}
                className="rounded border border-dashed border-slate-700 px-2 py-1 text-slate-400 hover:bg-slate-800"
              >
                + Add team task
              </button>
            )
          )}
        </div>
      </div>
      <div className="max-h-[520px] overflow-y-auto p-2">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="w-8 text-left">#</th>
              <th className="text-left">Player</th>
              <th className="w-16 text-right">M1</th>
              <th className="w-20 text-right">Total</th>
              <th className="w-28 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {team.assignments
              .filter((a) => a.player)
              .slice()
              .sort(
                (a, b) => (b.player!.totalPower ?? 0) - (a.player!.totalPower ?? 0),
              )
              .map((a, i) => (
                <tr key={a.id} className="border-t border-slate-900/60">
                  <td className="py-1 text-slate-500">{i + 1}</td>
                  <td className="py-1 truncate">
                    <span className="flex items-center gap-1">
                      <span className="truncate">
                        {playerLabel(a.player)}
                      </span>
                      {team.leaderPlayerId === a.playerId && (
                        <span
                          title="Team leader"
                          className="rounded bg-amber-900/50 px-1 text-[10px] font-semibold text-amber-200"
                        >
                          LEAD
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="py-1 text-right tabular-nums text-slate-300">
                    {formatPower(a.player!.m1Power ?? 0)}
                  </td>
                  <td className="py-1 text-right tabular-nums text-slate-300">
                    {formatPower(a.player!.totalPower ?? 0)}
                  </td>
                  <td className="py-1">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        disabled={readOnly}
                        onClick={() => onToggleLock(a)}
                        className={cn(
                          'rounded border px-1.5 py-0.5 text-xs',
                          a.locked
                            ? 'border-amber-700 bg-amber-900/40 text-amber-200'
                            : 'border-slate-700 text-slate-400 hover:bg-slate-800',
                          readOnly && 'opacity-40',
                        )}
                        title={a.locked ? 'Pinned to team' : 'Pin to team'}
                      >
                        {a.locked ? <Lock size={12} /> : <LockOpen size={12} />}
                      </button>
                      <button
                        disabled={readOnly}
                        onClick={() => setMoving(a)}
                        className="rounded border border-slate-700 px-1.5 py-0.5 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
                        title="Move to another team"
                      >
                        <ArrowRightLeft size={12} />
                      </button>
                      <button
                        disabled={readOnly}
                        onClick={() => onAssign(a.position, null)}
                        className="rounded border border-slate-700 px-1.5 py-0.5 text-slate-400 hover:bg-slate-800 hover:text-red-300 disabled:opacity-40"
                        title="Remove from team"
                      >
                        <X size={12} />
                      </button>
                      <AttendanceButtons
                        value={a.attendance}
                        disabled={attendanceLocked}
                        onChange={(v) => onSetAttendance(a.id, v)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {!readOnly && slotPoolBase.length > 0 && (
          <div className="mt-2 border-t border-slate-900/60 pt-2">
            <SpecialPlayerPicker
              players={slotPoolBase}
              selectedId={null}
              onSelect={(pid) => {
                if (pid == null) return;
                const empty = team.assignments.find((a) => a.playerId == null);
                const maxPos = team.assignments.reduce(
                  (m, a) => Math.max(m, a.position),
                  0,
                );
                const position = empty ? empty.position : maxPos + 1;
                onAssign(position, pid);
              }}
              placeholder="+ Add player…"
              compact
            />
          </div>
        )}
      </div>
      {moving && moving.playerId != null && (
        <MoveToTeamModal
          player={moving.player}
          currentTeamId={team.id}
          teams={allTeams}
          onMove={(toTeamId) => {
            onMoveToTeam(moving.playerId!, toTeamId);
            setMoving(null);
          }}
          onClose={() => setMoving(null)}
        />
      )}
      {editingTeam && (
        <EditTeamModal
          team={team}
          shareSum={shareSum}
          onSave={(body) => {
            onEditTeam(body);
            setEditingTeam(false);
          }}
          onClose={() => setEditingTeam(false)}
        />
      )}
    </div>
  );
}

function SpecialEventCountdown({
  eventDate,
  startsAt,
}: {
  eventDate: string;
  startsAt: string;
}) {
  const targetMs = useMemo(() => {
    const [y, m, d] = eventDate.split('-').map(Number);
    const [hh, mm] = startsAt.split(':').map(Number);
    if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;
    return Date.UTC(y, m - 1, d, hh, mm) - SERVER_UTC_OFFSET_MINUTES * 60_000;
  }, [eventDate, startsAt]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (targetMs == null) return null;
  const delta = targetMs - now;
  const past = delta <= 0;
  const abs = Math.abs(delta);
  const totalSec = Math.floor(abs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  let label: string;
  if (days > 0) label = `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  else label = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold tabular-nums',
        past
          ? 'bg-slate-800 text-slate-400'
          : delta < 60 * 60_000
            ? 'bg-red-700/50 text-red-100'
            : delta < 24 * 60 * 60_000
              ? 'bg-amber-700/50 text-amber-100'
              : 'bg-indigo-700/50 text-indigo-100',
      )}
      title={past ? 'Event start passed' : 'Time until start (server time)'}
    >
      {past ? '−' : ''}
      {label}
    </span>
  );
}

function EditTeamModal({
  team,
  shareSum,
  onSave,
  onClose,
}: {
  team: SpecialEventTeam;
  shareSum: number;
  onSave: (body: {
    name?: string | null;
    capacity?: number;
    strengthShare?: number;
  }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(team.name ?? '');
  const [capacity, setCapacity] = useState(team.capacity);
  const [error, setError] = useState<string | null>(null);
  const pct = shareSum > 0 ? Math.round((team.strengthShare / shareSum) * 100) : 0;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md space-y-3 rounded-lg border border-slate-800 bg-slate-950 p-6 shadow-2xl">
        <h2 className="text-base font-semibold">
          Edit {team.name?.trim() || `Team ${team.teamIndex}`}
        </h2>
        <label className="block space-y-1 text-sm">
          <span className="text-xs text-slate-400">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`Team ${team.teamIndex}`}
            className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-xs text-slate-400">
            Capacity (currently assigned: {team.totals.assigned})
          </span>
          <input
            type="number"
            min={1}
            value={capacity}
            onChange={(e) => setCapacity(Number(e.target.value) || 0)}
            className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm"
          />
        </label>
        <div className="text-xs text-slate-400">
          Strength share: <strong className="text-slate-200">{pct}%</strong>
        </div>
        {error && <div className="text-sm text-red-400">{error}</div>}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (capacity < 1) {
                setError('Capacity must be at least 1');
                return;
              }
              onSave({
                name: name.trim() === '' ? null : name.trim(),
                capacity,
              });
            }}
            className="rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function AttendanceButtons({
  value,
  disabled,
  onChange,
}: {
  value: SpecialAttendance | null;
  disabled?: boolean;
  onChange: (v: SpecialAttendance | null) => void;
}) {
  const toggle = (v: SpecialAttendance) => onChange(value === v ? null : v);
  return (
    <div className="flex gap-0.5">
      <button
        disabled={disabled}
        onClick={() => toggle('showed')}
        title="Attended"
        className={cn(
          'rounded p-1',
          value === 'showed'
            ? 'bg-emerald-600 text-white'
            : 'text-slate-500 hover:bg-slate-800 hover:text-emerald-400',
          disabled && 'opacity-40',
        )}
      >
        <Check size={12} />
      </button>
      <button
        disabled={disabled}
        onClick={() => toggle('rulebreaker')}
        title="Rulebreaker"
        className={cn(
          'rounded p-1',
          value === 'rulebreaker'
            ? 'bg-amber-600 text-white'
            : 'text-slate-500 hover:bg-slate-800 hover:text-amber-400',
          disabled && 'opacity-40',
        )}
      >
        <ShieldAlert size={12} />
      </button>
      <button
        disabled={disabled}
        onClick={() => toggle('no_show')}
        title="No-show"
        className={cn(
          'rounded p-1',
          value === 'no_show'
            ? 'bg-red-700 text-white'
            : 'text-slate-500 hover:bg-slate-800 hover:text-red-400',
          disabled && 'opacity-40',
        )}
      >
        <X size={12} />
      </button>
    </div>
  );
}

function MoveToTeamModal({
  player,
  currentTeamId,
  teams,
  onMove,
  onClose,
}: {
  player: SpecialEventPlayer | null;
  currentTeamId: number;
  teams: SpecialEventTeam[];
  onMove: (toTeamId: number) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md space-y-3 rounded-lg border border-slate-800 bg-slate-950 p-6 shadow-2xl">
        <h2 className="text-base font-semibold">
          Move {playerLabel(player)} to…
        </h2>
        <p className="text-xs text-slate-400">
          The player is placed in the first empty slot of the target team. When
          the team is full, the player still moves — the team badge turns red
          until you rebalance.
        </p>
        <div className="space-y-2">
          {teams
            .filter((t) => t.id !== currentTeamId)
            .map((t) => {
              const label = t.name?.trim() || `Team ${t.teamIndex}`;
              const full = t.totals.assigned >= t.capacity;
              return (
                <button
                  key={t.id}
                  onClick={() => onMove(t.id)}
                  className={cn(
                    'flex w-full items-center justify-between rounded border px-3 py-2 text-left text-sm hover:bg-slate-800',
                    full
                      ? 'border-red-800 bg-red-950/40 text-red-100'
                      : 'border-slate-700 bg-slate-900',
                  )}
                >
                  <span className="font-medium">{label}</span>
                  <span className="text-xs tabular-nums text-slate-400">
                    {t.totals.assigned}/{t.capacity}
                    {full && <span className="ml-1 text-red-300">(full)</span>}
                  </span>
                </button>
              );
            })}
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function EditHeaderModal({
  initial,
  onSave,
  pending,
  onClose,
}: {
  initial: { customName: string; eventDate: string; startsAt: string };
  onSave: (body: {
    customName: string;
    eventDate: string;
    startsAt: string | null;
  }) => void;
  pending: boolean;
  onClose: () => void;
}) {
  const [customName, setCustomName] = useState(initial.customName);
  const [eventDate, setEventDate] = useState(initial.eventDate);
  const [startsAt, setStartsAt] = useState(initial.startsAt);
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md space-y-3 rounded-lg border border-slate-800 bg-slate-950 p-6 shadow-2xl">
        <h2 className="text-lg font-semibold">Edit event</h2>
        <label className="block space-y-1 text-sm">
          <span className="text-xs text-slate-400">Name</span>
          <input
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm"
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1 text-sm">
            <span className="text-xs text-slate-400">Date</span>
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-xs text-slate-400">Time (optional)</span>
            <input
              type="time"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm"
            />
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              onSave({
                customName: customName.trim(),
                eventDate,
                startsAt: startsAt.trim() === '' ? null : startsAt,
              })
            }
            disabled={pending || !customName.trim()}
            className="rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function EditWeightsModal({
  teams,
  onSave,
  onClose,
}: {
  teams: SpecialEventTeam[];
  onSave: (rows: { teamId: number; strengthShare: number }[]) => void | Promise<void>;
  onClose: () => void;
}) {
  const [values, setValues] = useState<Record<number, number>>(() => {
    const out: Record<number, number> = {};
    for (const t of teams) out[t.id] = t.strengthShare;
    return out;
  });
  const [saving, setSaving] = useState(false);
  const sum = Object.values(values).reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0);
  const equalReset = () => {
    const next: Record<number, number> = {};
    for (const t of teams) next[t.id] = 1;
    setValues(next);
  };
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg space-y-3 rounded-lg border border-slate-800 bg-slate-950 p-6 shadow-2xl">
        <h2 className="text-base font-semibold">Team strength weights</h2>
        <p className="text-xs text-slate-400">
          Freeform weights that are normalized to 100%. Editing one team's
          weight automatically reduces every other team's percentage share.
        </p>
        <div className="rounded border border-slate-800 bg-slate-900/40 p-3">
          <div className="mb-2 grid grid-cols-[24px_1fr_120px_60px] items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
            <div>#</div>
            <div>Name</div>
            <div>Weight</div>
            <div className="text-right">%</div>
          </div>
          <div className="space-y-2">
            {teams.map((t) => {
              const raw = values[t.id] ?? 0;
              const pct = sum > 0 ? Math.round((raw / sum) * 100) : 0;
              return (
                <div
                  key={t.id}
                  className="grid grid-cols-[24px_1fr_120px_60px] items-center gap-2 text-sm"
                >
                  <div className="text-slate-400">{t.teamIndex}</div>
                  <div className="truncate">{t.name?.trim() || `Team ${t.teamIndex}`}</div>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={raw}
                    onChange={(e) =>
                      setValues({
                        ...values,
                        [t.id]: Number(e.target.value) || 0,
                      })
                    }
                    className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm"
                  />
                  <div className="text-right tabular-nums text-slate-300">{pct}%</div>
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <span>Sum = {sum}</span>
            <button
              type="button"
              onClick={equalReset}
              className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
            >
              Reset to equal split
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            disabled={saving || sum <= 0}
            onClick={async () => {
              setSaving(true);
              try {
                const rows = teams.map((t) => ({
                  teamId: t.id,
                  strengthShare: values[t.id] ?? 0,
                }));
                await onSave(rows);
              } finally {
                setSaving(false);
              }
            }}
            className="rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save weights'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ExportModal({
  data,
  overviewRef,
  teamCardRefs,
  onClose,
}: {
  data: SpecialEventDetailPayload;
  overviewRef: React.RefObject<HTMLDivElement | null>;
  teamCardRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  const downloadNode = async (node: HTMLElement | null, filename: string) => {
    if (!node) return;
    setBusy(filename);
    try {
      const dataUrl = await renderNodeToPng(node, { format: 'jpeg', quality: 0.9 });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setBusy(null);
    }
  };

  const stamp = data.event.eventDate;
  const slug = (data.event.customName ?? 'special-event')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/60 p-4">
      <div className="my-8 w-full max-w-6xl space-y-4 rounded-lg border border-slate-800 bg-slate-950 p-6 shadow-2xl">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">Export images</h2>
          <button
            onClick={onClose}
            className="ml-auto rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
          >
            <X size={14} />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => downloadNode(overviewRef.current, `${slug}-${stamp}-overview.jpg`)}
            disabled={!!busy}
            className="inline-flex items-center gap-1 rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500 disabled:opacity-50"
          >
            <Download size={12} /> Download overview
          </button>
          {data.teams.map((t) => (
            <button
              key={t.id}
              onClick={() =>
                downloadNode(
                  teamCardRefs.current[t.id],
                  `${slug}-${stamp}-team${t.teamIndex}.jpg`,
                )
              }
              disabled={!!busy}
              className="inline-flex items-center gap-1 rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800 disabled:opacity-50"
            >
              <Download size={12} /> {t.name?.trim() || `Team ${t.teamIndex}`}
            </button>
          ))}
        </div>
        <div className="rounded border border-slate-800 bg-slate-900/40 p-2 text-xs text-slate-400">
          Previews below render exactly what is exported (JPEG, ~1080 px wide).
        </div>
        <div className="space-y-6">
          <div>
            <div className="mb-2 text-xs uppercase tracking-wider text-slate-500">Overview</div>
            <div className="overflow-x-auto">
              <SpecialEventOverview innerRef={overviewRef} data={data} />
            </div>
          </div>
          {data.teams.map((t) => (
            <div key={t.id}>
              <div className="mb-2 text-xs uppercase tracking-wider text-slate-500">
                {t.name?.trim() || `Team ${t.teamIndex}`}
              </div>
              <SpecialEventTeamCard
                innerRef={(el) => {
                  teamCardRefs.current[t.id] = el;
                }}
                event={data.event}
                team={t}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
